import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Breadcrumbs,
  Tooltip,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
  Stack,
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  FormControl,
  InputAdornment,
  InputLabel,
  Input,
  FormHelperText,
  Backdrop,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import ErrorIcon from "@mui/icons-material/Error";
import React, { useEffect, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import ResponsiveDrawer from "../components/ClippedResponsiveDrawer";
import GetCustomerSpecific from "../utils/GetCustomer";
import CustomerContacts from "../components/CustomerContact";
import EquipmentUnit from "../components/EquipmentUnit";
import SeasonalityInfo from "../components/SeasonalityUnit";
import SettlementUnit from "../components/SettlementUnit";
import VisitUnit from "../components/VisitUnit";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import { TextFieldData } from "../componentUtils/TextFields";
import { MdCustomer } from "../../../dist/generated/MdCustomer";
import requestMissingContexts, { determineConfig } from "../utils/GetContext";
import { useLocalStorage } from "../componentUtils/LocalStorage";
import DotsMobileStepper from "../components/Stepper";
import BankSelector from "../components/BankableSelector";
import BankOverview from "../components/BankOverview";
import {  ProvideBankOverviewRecommendation } from "src/utils/GetEquipmentInfoAll";
import DexUnit from "../components/DexUnit";
import VPSettlement from "../components/VPSettlement";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomerNotesDisp from "../components/CustomerNotes";
import { checkForRoles } from "../utils/GetAuth";
import { PostRequeueRequest } from "../utils/PostCustomer";
import LoadingButton, { LoadingStatus } from "../components/LoadingButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteBank from "../utils/DeleteBank";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import PostBank from "../utils/PostBank";
import AlertDialog from "../componentUtils/AlertDialog";
import { Prompt } from "react-router-dom";
import ServicedGroupPage from "src/components/ServicedGroup";
import DialogComponent from "src/componentUtils/DialogComponent";
import DeleteGroup, { PostGrouped } from "src/utils/GetGroup";
import { MdServiceGroupedCustomer } from "../../../dist/generated/MdServiceGroupedCustomer";
import { DisplayRoleContext } from "src/Context_api";
import CustomerContractsDisp from "../components/CustomerContracts";

/**
 * This will be used for
 * @date 6/9/2022 - 5:03:06 PM
 * @author Srinadh Kanugala
 *
 * @interface CustomerRouteParams
 * @typedef {CustomerRouteParams}
 */
interface CustomerRouteParams {
  /**
   * This will be used for
   * @date 6/9/2022 - 5:03:06 PM
   * @author Srinadh Kanugala
   *
   * @type {string}
   */
  identifier: string;
}

/**
 * This will be used for
 * @date 6/9/2022 - 5:03:06 PM
 * @author Srinadh Kanugala
 *
 * @type {*}
 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    accordion: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    breadCrumbs: {
      paddingBottom: theme.spacing(2),
    },
    centerDiv: {
      textAlign: "center",
    },
    root: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    rootContainer: {
      [theme.breakpoints.down("md")]: {
        padding: 0,
        paddingTop: theme.spacing(1),
      },
    },
    gridSpaceable: {
      padding: theme.spacing(2),
    },
    gridMarginTop: {
      marginTop: theme.spacing(2),
    },
    chipActual: {
      margin: theme.spacing(2, 1, 2, 1),
    },
    chipOnline: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    mapContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    padVertical: {
      paddingTop: theme.spacing(1),
    },
    buttonAllGroup: {
      marginBottom: theme.spacing(2),
    },
  })
);

/**
 * This will be used for
 * @date 6/9/2022 - 5:03:06 PM
 * @author Srinadh Kanugala
 *
 * @class CustomerAdminState
 * @typedef {CustomerAdminState}
 */
class CustomerAdminState {
  /**
   * This will be used for
   * @date 6/9/2022 - 5:03:06 PM
   * @author Srinadh Kanugala
   *
   * @type {LoadingStatus}
   */
  requeue: LoadingStatus = LoadingStatus.Initial;

  /**
   * This will be used for
   * @date 6/9/2022 - 5:03:06 PM
   * @author Srinadh Kanugala
   *
   * @type {string}
   */
  requeueMsg: string = "";
}
/**
 * This will be used for 
 * @date 6/9/2022 - 5:03:06 PM
 * @author Srinadh Kanugala
 *
 * @param {{
  clientId: string;
  salesOrg: string;
  customerNum: string;
}} props
 * @returns {*}
 */
function CustomerAdmin(props: { clientId: string; salesOrg: string; customerNum: string }) {
  const classes = useStyles();
  const [state, setState] = React.useState<CustomerAdminState>(new CustomerAdminState());

  async function postQueue() {
    setState({ ...state, requeue: LoadingStatus.Loading });
    PostRequeueRequest(props.clientId, props.salesOrg, props.customerNum)
      .then((resp) => {
        setState({
          ...state,
          requeue: LoadingStatus.Success,
          requeueMsg: resp.data.msg || "Success",
        });
      })
      .catch((err) => {
        setState({
          ...state,
          requeue: LoadingStatus.Error,
          requeueMsg: err.response.data.msg || "Error",
        });
      });
  }
  return (
    <div>
      <Grid container>
        <Grid item xs={12}>
          <Typography color="textPrimary" variant="h4">
            Reproccess Settlement Queue
          </Typography>
          <Typography color="textPrimary" variant="body1">
            Manually request the backend system to reprocess the customer's invoices to redetermine the planning data.
          </Typography>
        </Grid>
        <Grid item xs={2}>
          <Grid container className={classes.buttonAllGroup}>
            <Grid item xs={12}>
              <LoadingButton
                title="Requeue"
                status={state.requeue}
                disabled={LoadingStatus.Initial !== state.requeue}
                tooltip=""
                loadFn={() => {
                  postQueue();
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {state.requeueMsg !== "" ? (
            <Alert severity={state.requeue === LoadingStatus.Error ? "warning" : "success"}>
              <Typography variant="h6">{state.requeueMsg}</Typography>
            </Alert>
          ) : (
            <div />
          )}
        </Grid>
      </Grid>
    </div>
  );
}

/**
 * This will be used for 
 * @date 6/9/2022 - 5:03:06 PM
 * @author Srinadh Kanugala
 *
 * @export
 * @param {{
  customerNum?: string;
  headerDisabled?: boolean;
}} props
 * @returns {*}
 */

export default function CustomerPage(props: { customerNum?: string; headerDisabled?: boolean }) {
  const classes = useStyles();

  const {isDisplayOnlyRole}: any = useContext(DisplayRoleContext)
  const [state, setState] = React.useState<any | undefined>();
  const [codes, setCodes] = React.useState<any>();
  const [doneLoading, setDoneLoading] = React.useState<boolean>(false);
  const [editBankLabel, setEditBankLabel] = React.useState<boolean>(false);
  const [editGroupLabel, setEditGroupLabel] = React.useState<boolean>(false);
  const [editBankCust, setEditBankCust] = React.useState<boolean>(false);
  const [customerLoaded, setCustomerLoaded] = useLocalStorage<string>("SETTINGS.CP_customerNum", "");
  const [tabIndex, setTabIndex] = useLocalStorage("SETTINGS.CP_tabIndex", 0);
  const [clientId] = useLocalStorage("SETTINGS.clientId", "100");
  const [salesOrg] = useLocalStorage("SETTINGS.salesOrg", "");
  const history = useHistory();
  const [bankName, setBankName] = React.useState<string>("");
  const [groupName, setGroupName] = React.useState<string>("");
  const [bankCustomers, setBankCustomers] = React.useState<string[]>([]);
  const [groupedCustomers, setGroupedCustomers] = React.useState<string[]>([]);
  const [bankCustomersDetails, setBankCustomersDetails] = React.useState<any>([]);
  const [groupCustomersDetails, setGroupCustomersDetails] = React.useState<any>([]);
  const routeParams = useParams<CustomerRouteParams>();
  const [error, setError] = React.useState<boolean>(false);
  const [helperText, setHelperText] = React.useState<string>("");
  const [groupHelperText, setGroupHelperText] = React.useState<string>("");
  const customerNum: string = props.customerNum ? props.customerNum : routeParams.identifier;
  const [openAlertDialog, setOpenAlertDialog] = React.useState<boolean>(false);
  const [tabState, setTabState] = React.useState(customerNum !== customerLoaded ? 0 : tabIndex);
  const [unsavedChanges, setUnsavedChanges] = React.useState<boolean>(false);
  const [unsavedGroupChanges, setUnsavedGroupChanges] = React.useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);
  const [groupConfirmDelete, setGroupConfirmDelete] = React.useState<boolean>(false);
  const [newTabIndex, setNewTabIndex] = React.useState<any>(null);
  const [changed, setChanged] = React.useState<boolean>(false);
  const [groupDetailsChanged, setGroupDetailsChanged] = React.useState<boolean>(false);
  const [Confirmpop, setConfirmpop] = React.useState<boolean>(false);
  const [ConfirmpopUpdate, setUpdateConfirmpop] = React.useState<boolean>(false);
  const [ErrorPop, setErrorPop] = React.useState<boolean>(false);
  const [ConfirmDuplicateCust, setUpdateConfirmDuplicateCust] = React.useState<boolean>(false);
  const [ConfirmGroupDuplicateCust, setUpdateGroupConfirmDuplicateCust] = React.useState<boolean>(false);
  const [openBankNameDialog, setOpenBankNameDialog] = React.useState<boolean>(false);
  const [openGroupNameDialog, setOpenGroupNameDialog] = React.useState<boolean>(false);
  const [warningPopUp, setWarningPopUp] = React.useState<boolean>(false);
  const [groupWarningPopUp, setGroupWarningPopUp] = React.useState<boolean>(false);
  const [DuplicateCust, setDuplicateCust] = React.useState<any>([]);
  const [groupDuplicateCust, setGroupDuplicateCust] = React.useState<any>([]);
  const [backdropLoader, setBackdropLoader] = React.useState<boolean>(false);
  const [alertInfo, setAlertInfo] = React.useState<any>({ severity: "", message: "" });
  const [overview,setOverview]=React.useState<any>({
    bankedRecos: []
  });
  const [isRecoLoaded,setIsRecoLoaded]=React.useState(false);
  const [duplicateGroupName, setDuplicateGroupName] = React.useState<boolean>(false);
  const [duplicateBankName, setDuplicateBankName] = React.useState<boolean>(false);
  
  
  const urlParams = new URLSearchParams(window.location.search)
  // useEffect(() => {
  window.onbeforeunload = (event) => {
    const e = event || window.event;
    // Cancel the event
    if (
      tabState === 1 &&
      (bankName !== bankText ||
        JSON.stringify(bankCustomers) !== JSON.stringify(bankedStr?.split(",").map((item: any) => item.trim())))
    ) {
      e.preventDefault();
      if (e) {
        e.returnValue = ""; // Legacy method for cross browser support
      }
      return (e.returnValue = "Are you sure you want to exit?");
    }else if(tabState === 9 && (
      groupName !== groupText ||
      JSON.stringify(groupedCustomers) !== JSON.stringify(groupedStr?.split(",").map((item: any) => item.trim()))
    )){
      e.preventDefault()
      if(e) {
        e.returnValue = "";
      }
      return (e.returnValue = "Are you sure you want to exit")
    }
    setCustomerLoaded("");
  };
  // });
  // const setCurrent = () => {
  //   // let cur:any = tableMeta.currentTableData[tableMeta.rowIndex];

  // }
  // const handleAlertClose = () => {
  //   document.location.reload()
  // }

  useEffect(() => {
    if (doneLoading) return;
    /** Getting history state (index) value from customer list bank check box.
     * Assigning the value to tab index and setTabState*/
    const tabIndexValue = history.location?.state ? parseInt(history.location?.state + "", 10) : 0;

    
   
      // setTabIndex(tabIndexValue);
      // setTabState(tabIndexValue);
   
    let tabId:number=Number(urlParams.get('tab'))||0
    if(tabId==6){
      setTabIndex(tabId);
      setTabState(tabId);
    }
    /**Setting up tab index from customer list bank checkbox done */
    requestMissingContexts(["FSVSUBTRADECHANNEL", "SALESOFFICES", "FSVTRADECHANNEL"]).then((v) => {
      setCodes(v);
    });

    GetCustomerSpecific(salesOrg.toString(), customerNum).then((v: MdCustomer) => {
      if (v) {
        setState({
          ...v,
        });
      }
      setDoneLoading(true);
    });
  }, [state, doneLoading]);

  const checkForBankChanges = () => {
    if (tabState === 1 && bankCustomers?.length > 1) {
      if (bankName !== bankText || !bankedStr?.length) {
        return true;
      } else if (bankedStr?.length > 0) {
        if (JSON.stringify(bankCustomers) !== JSON.stringify(bankedStr?.split(",").map((item: any) => item.trim()))) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else if (bankedStr?.length > 0) {
      if (JSON.stringify(bankCustomers) !== JSON.stringify(bankedStr?.split(",").map((item: any) => item.trim()))) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };
  const checkForGroupChanges = () => {
    if (tabState === 9 && groupedCustomers?.length > 1) {
      if (groupName !== groupText || !groupedStr?.length) {
        return true;
      } else if (groupedStr?.length > 0) {
        if (JSON.stringify(groupedCustomers) !== JSON.stringify(groupedStr?.split(",").map((item: any) => item.trim()))) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else if (groupedStr?.length > 0) {
      if (JSON.stringify(groupedCustomers) !== JSON.stringify(groupedStr?.split(",").map((item: any) => item.trim()))) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    const res = checkForBankChanges();
    setChanged(res);
  }, [bankName, bankCustomers]);

  useEffect(() => {
    const res = checkForGroupChanges();
    setGroupDetailsChanged(res);
  }, [groupName, groupedCustomers]);

  useEffect(()=>{
    if (bankedCustomers.length > 0 && !isRecoLoaded) {
      setIsRecoLoaded(true);
      setBackdropLoader(true);
      bankOverviewResponse().then((v: any) => {
        let recommendation=v.filter((item:any)=>!item.errorMessage);
        setOverview({...overview,bankedRecos:recommendation})
        setBackdropLoader(false);
      });
    }
  },[state]);

  const bankOverviewResponse = async () => {
    return await ProvideBankOverviewRecommendation(
      salesOrg.toString(),
      customerNum,
      false
    );
  };
  const disabledTextFields: TextFieldData[] = [
    {
      title: "Customer Number",
      field: "customerNum",
      md: 6,
      lg: 3,
    },
    {
      title: "Name",
      field: "name1",
      md: 10,
      lg: 5,
    },
     {
      title: "Customer Type",
      field: "customer_type",
      md: 3,
      lg: 2,
    },
    {
      title: "Timezone",
      field: "cust_timezone",
      md: 3,
      lg: 2,
    },
    {
      title: "Street",
      field: "street",
      md: 12,
      lg: 6,
    },
    {
      title: "City",
      field: "city",
      md: 6,
      lg: 3,
    },
    {
      title: "State",
      field: "state",
      md: 3,
      lg: 1,
    },
    {
      title: "Zipcode",
      field: "zipcode",
      md: 3,
      lg: 2,
    },
    {
      title: "Delivering Plant",
      field: "deliveringPlant",
      md: 3,
      lg: 1,
    },
    {
      title: "Master Route",
      field: "masterRoute",
      md: 3,
      lg: 1,
    },
    {
      title: "Incl Mat List ID",
      field: "authMaterialListId",
      md: 3,
      lg: 2,
    },
        {
      title: "Excl Mat List ID",
      field: "exclMaterialListId",
      md: 3,
      lg: 2,
    },
  ];

  const handleDelete = async () => {
    if (!state) return;
    const salesOrg = state.salesOrg;
    const bankGuid = state.bankName.guid;
    // console.log("salesorg and guid",salesOrg , bankGuid);
    // let obj=JSON.parse(JSON.stringify(state));
    //("---", state.bankName.guid);
    // let bankGuid=state.bankName.guid;
    // debugger;
    // const bankGuid = obj['bankName'].guid;
    const response = await DeleteBank(salesOrg, bankGuid);
   // console.log("response", response);
    if (response.message == "done") {
      window.location.reload();
    } else {
      <Alert severity="error">
        <AlertTitle>
          <Typography variant="h4">Failed to delete the Bank. Please try again </Typography>
        </AlertTitle>
      </Alert>;
    }
  };

  const handleGroupDelete = async () => {
    if(!state) return;
    const _salesOrg = state.salesOrg;
    const _groupGuid = state.groupName.guid;
    const response = await DeleteGroup(_salesOrg, _groupGuid);
    if(response.message == "done"){
      window.location.reload();
    } else {
      <Alert severity="error">
        <AlertTitle>
          <Typography variant="h4">Failed to delete the Group. Please try again </Typography>
        </AlertTitle>
      </Alert>;
    }
  }

  const handleSave = async () => {};

  const bankedCustomers = (state && state.mdBankCustomers && (state as any).bankedCustomers) || [];
  const groupCustomers = (state && state.mdServiceGroupedCustomer && (state as any).groupedCustomers) || [];

  const bankText =
    (state && state.mdBankCustomers && (state as any).bankName && (state as any).bankName.bankName) || "";
  
  const groupText = (state && state.mdServiceGroupedCustomer && (state as any).groupName && (state as any).groupName.groupName) || "";
  React.useEffect(() => {
    if (bankText?.length > 0 && bankName === "") {
      setBankName(bankText);
    }
  }, [bankText]);

  React.useEffect(() => {
    if (groupText?.length > 0 && groupName === "") {
      setGroupName(groupText);
    }
  }, [groupText]);

  const groupedStr: any =
  (groupCustomers.length > 0 && groupCustomers.map((b: any) => b.customerNum).join(", ")) || [];
React.useEffect(() => {
  if (groupedStr?.length > 0) {
    setGroupedCustomers(groupedStr?.split(",").map((item: any) => item.trim()));
  }
}, [groupedStr]);

  useEffect(() => {
    if (bankName === "") {
      setHelperText("Bank name may not be empty.");
      setError(true);
    } else if (bankName?.length >= 0 && bankName?.length < 3) {
      setHelperText("Bank name must be atleast 3 characters.");
      setError(true);
    } else if (bankName?.length > 20) {
      setHelperText("Bank name should not exceed 20 characters.");
      setError(true);
    } else {
      setHelperText("");
      setError(false);
    }
  }, [bankName]);

  useEffect(() => {
    if (groupName === "") {
      setGroupHelperText("Group name may not be empty.");
      setError(true);
    } else if (groupName?.length >= 0 && groupName?.length < 3) {
      setGroupHelperText("Group name must be atleast 3 characters.");
      setError(true);
    } else if (groupName?.length > 20) {
      setGroupHelperText("Group name should not exceed 20 characters.");
      setError(true);
    } else {
      setGroupHelperText("");
      setError(false);
    }
  }, [groupName]);

  useEffect(() => {
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 420 ~ CustomerPage ~ bankCustomers", bankCustomers);
  }, [bankCustomers]);

  const postBankChanges = (salesOrg: string, bankUpdate: any) => {
    if (bankCustomers?.length === 0) {
      setWarningPopUp(true);
    } else if (bankName === "") {
      setBackdropLoader(true);
      PostBank(salesOrg, bankUpdate)
        .then((v: any) => {
          setOpenAlertDialog(true);
          setAlertInfo({ severity: "success", message: "Bank is succesfully saved" });
          setBackdropLoader(false);
        })
        .catch((error: any) => {
          setBackdropLoader(false);
          if(error?.response?.status == 409){
            setDuplicateBankName(true);
            return;
          }
          if (error.response.status == 422) {
            setBackdropLoader(false);
            const customerNumbers = error.response.data.existingBanks[0].mdBankCustomers;
            const customerList = [];
            for (let i = 0; i <= customerNumbers.length; i++) {
              //console.log("svbjs bjsd v", customerNumbers[i]?.customerNum);
              if (customerNumbers[i]?.customerNum !== undefined) {
                customerList.push(customerNumbers[i]?.customerNum);
              }
            }
            setDuplicateCust(customerList);

           // console.log("error--->", customerList);
            setUpdateConfirmDuplicateCust(true);
          } else {
            //console.log("Failed to update the bank");
          }
          //console.log("Failed to update the bank");
        });
    } else {
      setBackdropLoader(true);
      bankUpdate.bankName = bankName;
      //console.log("else block ");
      PostBank(salesOrg, bankUpdate)
        .then((v: any) => {
          //console.log("post api response", v);
          setBackdropLoader(false);
          setUpdateConfirmpop(true);
          GetCustomerSpecific(salesOrg.toString(), customerNum).then((v: MdCustomer) => {
            if (v) {
              setState({
                ...v,
              });
            }
            setDoneLoading(true);
          });
        })
        .catch((error: any) => {
          setBackdropLoader(false);
          if(error?.response?.status == 409){
            setDuplicateBankName(true);
            return;
          }
          if (error.response.status == 422) {
            setBackdropLoader(false);
            const customerNumbers = error.response.data.existingBanks[0].mdBankCustomers;
            const customerList = [];
            for (let i = 0; i <= customerNumbers.length; i++) {
              //console.log("svbjs bjsd v", customerNumbers[i]?.customerNum);
              if (customerNumbers[i]?.customerNum !== undefined) {
                customerList.push(customerNumbers[i]?.customerNum);
              }
            }
            setDuplicateCust(customerList);

           // console.log("error--->", customerList);
            setUpdateConfirmDuplicateCust(true);
          } else {
           // console.log("Failed to update the bank");
          }
         // console.log("Failed to update the bank");
        });
    }
  };

  const postGroupChanges = (salesOrg: string, groupUpdate: any) => {
    if(groupedCustomers?.length === 0){
      setGroupWarningPopUp(true)
    }else if(groupName === ""){
      setBackdropLoader(true)
      PostGrouped(salesOrg, groupUpdate)
        .then((v:any) => {
          setOpenAlertDialog(true)
          setAlertInfo({severity: "success", message: "Group is succesfully saved"});
          setBackdropLoader(false)
        })
        .catch((e:any) => {
          setBackdropLoader(false);
          if(e?.response?.status == 409){
            setDuplicateGroupName(true);
            return;
          }
          if(e.response.status == 422){
            const customerNumbers = e?.response?.data?.existingGroups[0].mdServiceGroupedCustomer
            const customerList = [];
            for(let i = 0; i<=customerNumbers.length; i++){
              if(customerNumbers[i]?.customerNum !== undefined){
                customerList.push(customerNumbers[i]?.customerNum)
              }
            }
            setGroupDuplicateCust(customerList);
            
            setUpdateGroupConfirmDuplicateCust(true);
          }else {
            alert('Failed to update the Group')
          }
          alert('Failed to update the Group')
        })
    }
    else {
      setBackdropLoader(true)
      groupUpdate.groupName = groupName
      PostGrouped(salesOrg, groupUpdate)
        .then((v:any) => {
          setBackdropLoader(false);
          setUpdateConfirmpop(true);
          GetCustomerSpecific(salesOrg.toString(), customerNum).then((v: MdCustomer) => {
            if (v) {
              setState({
                ...v,
              });
            }
            setDoneLoading(true);
          });
        })
        .catch((e:any) => {
          setBackdropLoader(false);
          if(e?.response?.status == 409){
            setDuplicateGroupName(true);
            return;
          }
          if(e.response.status == 422){
            const customerNumbers = e?.response?.data?.existingGroups[0].mdServiceGroupedCustomer
            const customerList = [];
            for(let i = 0; i<=customerNumbers.length; i++){
              if(customerNumbers[i]?.customerNum !== undefined){
                customerList.push(customerNumbers[i]?.customerNum)
              }
            }
            setGroupDuplicateCust(customerList);
            
            setUpdateGroupConfirmDuplicateCust(true);
          }else {
            alert('Failed to update the Group')
          }
          alert('Failed to update the Group')
        })
    }
  }
  const saveCustomerDetails = (v: any) => {
    setBankCustomersDetails(v);
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 650 ~ saveCustomerDetails ~ v", v);
  };
  const saveGroupCustomerDetails = (v: any) => {
    setGroupCustomersDetails(v);
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 650 ~ saveCustomerDetails ~ v", v);
  };
  const tabs = state && [
    {
      title: "Customer",
      active: state?.active,
      conditional: true,
      content: (
        <div>
          <Chip label={state?.active ? "Active" : "Inactive"} className={classes.chipActual} color="primary" />
          {bankedCustomers.length > 0 && <Chip label={"Banked"} className={classes.chipActual} color="primary" />}
          {!state?.customerErrors || (
            <Tooltip title={state?.reconciliationErrorInfo?.infoType ? state?.reconciliationErrorInfo?.infoMessage: ''}>
            <Chip
              label="Inconsistencies: Customer"
              className={classes.chipActual}
              icon={<ErrorIcon />}
              color="primary"
            />
            </Tooltip>
          )}
          {!state?.materialErrors || (
            <Tooltip title={state?.matReconciliationErrorInfo?.infoType ? state?.matReconciliationErrorInfo?.infoMessage: ''}>

            <Chip
              label="Inconsistencies: Materials"
              className={classes.chipActual}
              icon={<ErrorIcon />}
              color="primary"
            />
           </Tooltip>
          )}
          <Grid container spacing={2} className={classes.gridMarginTop}>
            {disabledTextFields.map((v: TextFieldData, i: number) => {
              const q = (state as any)[v.field];

              return (
                <Grid
                  item
                  key={`CustomerPage-disabledTextFields-${i}-grd`}
                  xs={v.md}
                  lg={v.lg}
                  className={classes.gridSpaceable}
                >
                  <TextField
                    fullWidth={true}
                    key={`CustomerPage-disabledTextFields-${i}`}
                    label={v.title}
                    value={q || ""}
                    defaultValue={q || ""}
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
              );
            })}

            <Grid item xs={3} lg={2} className={classes.gridSpaceable}>
              <TextField
                fullWidth={true}
                label="Trade Channel"
                value={determineConfig(codes, "FSVTRADECHANNEL", state.tradeChannel || "")}
                defaultValue={determineConfig(codes, "FSVTRADECHANNEL", state.tradeChannel || "")}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={3} lg={2} className={classes.gridSpaceable}>
              <TextField
                fullWidth={true}
                label="Sub Trade Channel"
                value={determineConfig(codes, "FSVSUBTRADECHANNEL", state.subtradeChannel || "")}
                defaultValue={determineConfig(codes, "FSVSUBTRADECHANNEL", state.subtradeChannel || "")}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          </Grid>
          <CustomerNotesDisp />
                
          <CustomerContractsDisp customerNum={state.customerNum}/>
          
          <CustomerContacts />
          {state?.latitude && state?.longitude && (
            <Accordion className={classes.accordion}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                <Typography>Location</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <MapContainer
                  className={classes.mapContainer}
                  center={[state?.latitude, state?.longitude]}
                  style={{ width: "600px", height: "400px" }}
                  zoom={15}
                >
                  <TileLayer
                    attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[state.latitude, state.longitude]}>
                    <Popup>{state.name1}</Popup>
                  </Marker>
                </MapContainer>
              </AccordionDetails>
            </Accordion>
          )}
          <VPSettlement customerNum={state.customerNum} salesOrg={salesOrg} />
        </div>
      ),
    },
    {
      title: "Bank",
      content: (
        <BankSelector
          customerNum={state.customerNum}
          postBankChanges={postBankChanges}
          bankCustomer={bankCustomers}
          setBankCustomer={setBankCustomers}
          isChanged={changed}
          saveCustomerDetails={saveCustomerDetails}
          setWarningPopUp={setWarningPopUp}
          setBackdropLoader={setBackdropLoader}
          openBankNameDialog={openBankNameDialog}
          setOpenBankNameDialog={setOpenBankNameDialog}
        />
      ),
      conditional: true,
    },
    {
      title: "Equipment",
      content: <EquipmentUnit customerData={state} customerNum={state.customerNum} />,
      conditional: true,
    },
    {
      title: "Seasonality",
      content: <SeasonalityInfo customerNum={state.customerNum}/>,
      conditional: true,
    },
    {
      title: "Visit Times",
      content: <VisitUnit />,
      conditional: true,
    },
    {
      title: "Settlement/Prediction",
      content: <SettlementUnit CustomerNum={state.customerNum} />,
      conditional: true,
    },
    {
      title: "Telemetry",
      content: <DexUnit DeliveryPlant = {state.deliveringPlant}/>,
      conditional: true,
    },
    {
      title: "Bank Overview",
      content: <BankOverview customer_num={state.customerNum} bankedRecommendations={overview.bankedRecos} />,
      conditional: bankedCustomers && bankedCustomers.length > 0,
    },
    {
      title: "Admin",
      content: <CustomerAdmin clientId={clientId} salesOrg={salesOrg} customerNum={customerNum} />,
      conditional: checkForRoles(clientId, salesOrg, ["vp_admin", "vs_admin"]),
    },
    {
      title: "Service Group",
      content: (
        <ServicedGroupPage
          customerNum={state.customerNum}
          plant={state.deliveringPlant}
          postGroupChanges={postGroupChanges}
          groupedCustomer={groupedCustomers}
          setGroupedCustomer={setGroupedCustomers}
          isChanged={groupDetailsChanged}
          saveCustomerDetails={saveGroupCustomerDetails}
          setWarningPopUp={setGroupWarningPopUp}
          setBackdropLoader={setBackdropLoader}
          openGroupNameDialog={openGroupNameDialog}
          setOpenGroupNameDialog={setOpenGroupNameDialog}
          isGroupCustomer={state?.groupName?.guid ? true: false}
        />
      ),
      conditional: true,
    },
  ];
  const bankedStr: any =
    (bankedCustomers.length > 0 && bankedCustomers.map((b: any) => b.customerNum).join(", ")) || [];
  React.useEffect(() => {
    if (bankedStr?.length > 0) {
      setBankCustomers(bankedStr?.split(",").map((item: any) => item.trim()));
    }
  }, [bankedStr]);

  const handleTabsChange = (_: React.ChangeEvent<{}>, value: any) => {
    const idx = tabs?.findIndex((v: any) => v.title === value) || 0;
    if (tabState === 1 && bankCustomers?.length > 1) {
      if (bankName !== bankText || !bankedStr?.length) {
        setUnsavedChanges(true);
        setNewTabIndex(idx);
      } else if (bankedStr?.length > 0) {
        if (JSON.stringify(bankCustomers) !== JSON.stringify(bankedStr?.split(",").map((item: any) => item.trim()))) {
          setUnsavedChanges(true);
          setNewTabIndex(idx);
        } else {
          setCustomerLoaded(customerNum);
          setTabIndex(idx);
          setTabState(idx);
        }
      } else {
        setCustomerLoaded(customerNum);
        setTabIndex(idx);
        setTabState(idx);
      }
    } else if(tabState === 9 && groupedCustomers?.length > 1){
      if(groupName !== groupText || !groupedStr?.length) {
        setUnsavedGroupChanges(true);
        setNewTabIndex(idx);
      } else if (groupedStr?.length > 0) {
        if (JSON.stringify(groupedCustomers) !== JSON.stringify(groupedStr?.split(",").map((item: any) => item.trim()))) {
          setUnsavedGroupChanges(true);
          setNewTabIndex(idx);
        } else {
          setCustomerLoaded(customerNum);
          setTabIndex(idx);
          setTabState(idx);
        }
      } else {
        setCustomerLoaded(customerNum);
        setTabIndex(idx);
        setTabState(idx);
      }
    }
     else {
      setCustomerLoaded(customerNum);
      setTabIndex(idx);
      setTabState(idx);
    }
  };

  const rerender = (customerNum: string, index: number) => {
    history.push(customerNum, index);
    setDoneLoading(false);
    setState(undefined);
  };

  const location = state && [state.street, state.city, state.state].filter((x) => !!x && x.length > 0).join(", ");

  var equiStr = "";
  var telemeterInfo = "";
  var dexDeviceInfo = "";
  if (state && state.mdEquipmentCustAssigns) {
    if (state.mdEquipmentCustAssigns.length > 0) {
      const activeAssignment = state.mdEquipmentCustAssigns[0];
      const equipment = activeAssignment.mdEquipment;
      if (equipment && equipment.mdEquipmentSubs) {
        if (equipment.mdEquipmentSubs.length > 0) {
          const telemeter = equipment.mdEquipmentSubs[0];
          if (telemeter.subSerialNum) {
            telemeterInfo = "Online: " + telemeter.subSerialNum;
          }
        }
      }
      if (equipment) {
        equiStr =
          " / " + equipment.assetNum + " / " + equipment.building + " " + equipment.room + " " + equipment.floor ;
        if (equipment.dexDeviceId) {
          dexDeviceInfo = "VM-DEX: " + equipment.dexDeviceId;
        }
      }
    }
  }
  const handleAddChip = (chip: any) => {
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 561 ~ handleAddChip ~ chip", chip);
  };
  const handleDeleteChip = (value: string) => {
    const newBankCustomers = bankCustomers.filter((item) => item !== value);
    setBankCustomers(newBankCustomers);
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 565 ~ handleDeleteChip ~ chip", value);
  };
  const handleGroupDeleteChip = (value: string) => {
    const newGroupCustomers = groupedCustomers.filter((item) => item !== value);
    setGroupedCustomers(newGroupCustomers);
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 565 ~ handleDeleteChip ~ chip", value);
  };
  const handleNoSaveChanges = () => {
    setUnsavedChanges(false);
    setCustomerLoaded(customerNum);
    setTabIndex(newTabIndex);
   // console.log("🚀 ~ file: CustomerPage.tsx ~ line 633 ~ handleNoSaveChanges ~ newTabIndex", newTabIndex);
    setTabState(newTabIndex);
    setEditBankCust(false);
    setEditBankLabel(false);
    setBankCustomers((bankedStr?.length > 0 && bankedStr?.split(",").map((item: any) => item.trim())) || []);
    setBankName(bankText);
  };
  const handleGroupNoSaveChanges = () => {
    setUnsavedGroupChanges(false);
    setCustomerLoaded(customerNum);
    setTabIndex(newTabIndex);
    //console.log("🚀 ~ file: CustomerPage.tsx ~ line 633 ~ handleNoSaveChanges ~ newTabIndex", newTabIndex);
    setTabState(newTabIndex);
    setEditGroupLabel(false);
    setGroupedCustomers((groupedStr?.length > 0 && groupedStr?.split(",").map((item: any) => item.trim())) || []);
    setGroupName(groupText);
  };
  const handleYesSaveChanges = () => {
    if (!bankName && !bankText) {
      setOpenBankNameDialog(true);
    } else {
      setUnsavedChanges(false);
      const obj: any = {
        ...state?.bankName,
        bankName: bankName || bankText,
        mdBankCustomers: bankCustomersDetails?.map((c: any) => ({
          ...c,
          salesOffice: c.sales_office,
          customerNum: c.customer_num,
        })),
      };
      postBankChanges(state?.salesOrg,obj);
     // console.log("🚀 ~ file: CustomerPage.tsx ~ line 659 ~ handleYesSaveChanges ~ obj", obj);
     // postGroupChanges(state?.salesOrg, obj);
    }
  };
  const handleGroupYesSaveChanges = () => {
    if (!groupName && !groupText) {
      setOpenGroupNameDialog(true);
    } else {
      setUnsavedGroupChanges(false);
      const obj: any = {
        ...state?.groupName,
        groupName: groupName || groupText,
        mdServiceGroupedCustomer: groupCustomersDetails?.map((c: any) => ({
          ...c,
          salesOffice: c.sales_office,
          customerNum: c.customer_num,
        })),
      };
     // console.log("🚀 ~ file: CustomerPage.tsx ~ line 659 ~ handleYesSaveChanges ~ obj", obj);
     postGroupChanges(state?.salesOrg, obj)
     //postBankChanges(state?.salesOrg, obj)
    }
  };

  const handleAlertClose = () => {
    // setOpenAlertDialog(false)
    // setAlertInfo({...alertInfo, severity: "", message: ""})
    document.location.reload();
  };
  //console.log("🚀 ~ file: CustomerPage.tsx ~ line 819 ~ CustomerPage ~ changed", changed);
  const content = (
    <div>
      <Prompt
        when={ (tabState === 1)? checkForBankChanges(): (tabState === 9)? checkForGroupChanges(): false}
        message={(location: any) => `Changes you made may not be saved.`}
      />
      <div className={classes.rootContainer}>
        {!!state && !!tabs ? (
          <div>
            <Paper className={classes.root}>
              {telemeterInfo && <Chip label={telemeterInfo} className={classes.chipOnline} color="primary" />}
              {dexDeviceInfo && <Chip label={dexDeviceInfo} className={classes.chipOnline} color="primary" />}
              <Breadcrumbs className={classes.breadCrumbs}>
                <Link color="inherit" href="/customers">
                  Customer List
                </Link>
                <Typography color="textPrimary" variant="body1">
                  {state.deliveringPlant}:{determineConfig(codes, "SALESOFFICES", state.deliveringPlant || "")} / Route{" "}
                  {state.masterRoute} / {state.customerNum} / {state.name1} {equiStr}
                </Typography>
              </Breadcrumbs>
              <Typography variant="body2">{location}</Typography>
              {state.mdBankCustomers.length > 0 && (
                <div>
                  {!editBankLabel ? (
                    <Typography variant="body2">
                      Bank Name: {bankName}
                      {tabState === 1 && (
                        <>
                          <Tooltip title={"Edit"}>
                            <IconButton
                              aria-label="edit"
                              color="primary"
                              disabled={isDisplayOnlyRole || editBankLabel}
                              onClick={() => setEditBankLabel(true)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Delete"}>
                            <IconButton aria-label="delete" color="primary" disabled={isDisplayOnlyRole}>
                              <DeleteIcon
                                fontSize="small"
                                onClick={() => {
                                  setConfirmDelete(true);
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* <Tooltip title={"Delete"}>
                      <IconButton aria-label="delete" color="primary">
                              <DeleteIcon fontSize="small"
                              onClick={() => {setConfirmpop(true);}}
                              />
                              </IconButton>
                            </Tooltip> */}
                    </Typography>
                  ) : (
                    <div>
                      <Typography variant="body2">
                        Bank Name:
                        <FormControl sx={{ margin: "0px 8px", width: "25ch" }} variant="standard">
                          <Input
                            value={bankName}
                            defaultValue={bankText}
                            placeholder="Please enter the Bank Name"
                            error={error}
                            onChange={(e) => setBankName(e.target.value)}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    if (bankName === "") setBankName(bankText);
                                    setEditBankLabel(false);
                                  }}
                                  disabled={error}
                                >
                                  <CheckIcon fontSize="small" color="primary" />
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    setBankName(bankText);
                                    setEditBankLabel(false);
                                  }}
                                >
                                  <ClearIcon fontSize="small" color="primary" />
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                          <FormHelperText id="helper-text">{helperText}</FormHelperText>
                        </FormControl>
                        <Tooltip title={"Edit"}>
                          <IconButton aria-label="edit" color="primary" disabled={isDisplayOnlyRole || editBankLabel}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={"Delete"}>
                          <IconButton aria-label="delete" color="primary" disabled={isDisplayOnlyRole || editBankLabel}>
                            <DeleteIcon
                              fontSize="small"
                              onClick={() => {
                                setConfirmDelete(true);
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                    </div>
                  )}
                  {/* {!editBankCust ? ( */}
                  <Typography variant="body2">
                    Banked Customers:{" "}
                    {tabState === 1
                      ? bankCustomers?.map((item) => (
                          <Chip
                            label={item}
                            color="primary"
                            disabled={isDisplayOnlyRole}
                            variant="outlined"
                            onDelete={() => handleDeleteChip(item)}
                          />
                        ))
                      : bankCustomers.join(",")}
                  </Typography>
                </div>
              )}
              {
                state.mdServiceGroupedCustomer?.length > 0 && (
                  <div>
                    {
                      !editGroupLabel ? (
                        <Typography variant="body2">
                      Service Group Name: {groupName}
                      {tabState === 9 && (
                        <>
                          <Tooltip title={"Edit"}>
                            <IconButton
                              aria-label="edit"
                              color="primary"
                              disabled={isDisplayOnlyRole || editGroupLabel}
                              onClick={() => setEditGroupLabel(true)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Delete"}>
                            <IconButton aria-label="delete" color="primary" disabled={isDisplayOnlyRole}>
                              <DeleteIcon
                                fontSize="small"
                                onClick={() => {
                                  setGroupConfirmDelete(true);
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Typography>
                      ): (
                      <div>
                        <Typography variant="body2">
                        Service Group Name:
                        <FormControl sx={{ margin: "0px 8px", width: "25ch" }} variant="standard">
                          <Input
                            value={groupName}
                            defaultValue={groupText}
                            placeholder="Please enter the Group Name"
                            error={error}
                            onChange={(e) => setGroupName(e.target.value)}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    if (groupName === "") setGroupName(groupText);
                                    setEditGroupLabel(false);
                                  }}
                                  disabled={error}
                                >
                                  <CheckIcon fontSize="small" color="primary" />
                                </IconButton>
                                <IconButton
                                  onClick={() => {
                                    setGroupName(groupText);
                                    setEditGroupLabel(false);
                                  }}
                                >
                                  <ClearIcon fontSize="small" color="primary" />
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                          <FormHelperText id="helper-text">{groupHelperText}</FormHelperText>
                        </FormControl>
                        <Tooltip title={"Edit"}>
                          <IconButton aria-label="edit" color="primary" disabled={isDisplayOnlyRole || editGroupLabel}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={"Delete"}>
                          <IconButton aria-label="delete" color="primary" disabled={isDisplayOnlyRole || editGroupLabel}>
                            <DeleteIcon
                              fontSize="small"
                              onClick={() => {
                                setConfirmDelete(true);
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Typography>
                      </div>)
                    }
                    <Typography variant="body2">
                    Service Group Customers:{" "}
                    {tabState === 9
                      ? groupedCustomers?.map((item) => (
                          <Chip
                            label={item}
                            color="primary"
                            disabled={isDisplayOnlyRole}
                            variant="outlined"
                            onDelete={() => handleGroupDeleteChip(item)}
                          />
                        ))
                      : groupedCustomers.join(",")}
                  </Typography>
                  </div>
                )
              }
            </Paper>
            <Paper className={classes.root}>
              <Typography variant="body2">
                Revenue / Day:{" "}
                <b>
                  {state.txVisitPlanning && state.txVisitPlanning.daily_revenue
                    ? `$${state.txVisitPlanning.daily_revenue.toFixed(2)}`
                    : "n/a"}
                </b>
              </Typography>
              <Typography variant="body2">
                Days until Stockout:{" "}
                <b>
                  {state.txVisitPlanning &&
                  state.txVisitPlanning.critical_material_inventory &&
                  state.txVisitPlanning.critical_material_consumption
                    ? Math.floor(
                        state.txVisitPlanning.critical_material_inventory /
                          state.txVisitPlanning.critical_material_consumption
                      )
                    : "n/a"}
                </b>
              </Typography>
              <Typography variant="body2">
              Effective Fill Rate:{" "}
                <b>
                  {state.txVisitPlanning && state.txVisitPlanning.effective_fill_rate
                    ? `${Math.min(state.txVisitPlanning.effective_fill_rate, 100).toFixed(2)}%`
                    : "n/a"}
                </b>
              </Typography>
              <Typography variant="body2">
                Vends / Day:{" "}
                <b>
                  {state.txVisitPlanning && state.txVisitPlanning.material_consumption
                    ? `${state.txVisitPlanning.material_consumption.toFixed(2)} units`
                    : "n/a"}
                </b>
              </Typography>
            </Paper>

            <Paper className={classes.root}>
              <Tabs onChange={handleTabsChange} value={tabs[tabState].title} variant="fullWidth">
                {tabs.map((tab: any) => tab.conditional && <Tab key={tab.title} label={tab.title} value={tab.title} />)}
              </Tabs>
              {tabs[tabState].content}
            </Paper>
            {state.mdBankCustomers && state.mdBankCustomers.length > 0 && (
              <DotsMobileStepper
                data={bankedCustomers}
                rerender={rerender}
                currentTabIndex={tabIndex as number}
                currentCustomer= {state.customerNum}
              />
            )}
          </div>
        ) : (
          <div>
            {doneLoading ? (
              <Alert severity="warning">
                <AlertTitle>
                  <Typography variant="h4">Customer Not Found</Typography>
                </AlertTitle>
                <Typography variant="body1">
                  The customer {customerNum} could not be found for bottler {salesOrg}.
                </Typography>
                <Typography variant="body1">
                  Either this customer belongs to a different bottler, or does not exist in the database.
                </Typography>
              </Alert>
            ) : (
              <div className={classes.centerDiv}>
                <CircularProgress color="primary" />
              </div>
            )}
          </div>
        )}
      </div>
      {/* Alerts Dialogs */}
      <AlertDialog
        severity={alertInfo?.severity}
        message={alertInfo?.message}
        open={openAlertDialog}
        handleClose={handleAlertClose}
      />
      {/* backdrop loader */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme: any) => theme.zIndex.drawer + 1 }} open={backdropLoader}>
        <CircularProgress color="primary" />
      </Backdrop>

      

      {/* warning pop for empty set */}
      <DialogComponent 
        open = {warningPopUp}
        title = {"Warning: No Banked Customers Found"}
        contentText = {"Bank group is empty; do you wish to remove bank?"}
        onConfirm = {handleDelete}
        onCancel = {() => {
          setWarningPopUp(false);
          setBankCustomers(bankedStr?.split(",").map((item: any) => item.trim()));
        }}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />
      {/* warning pop for empty set */}
      <DialogComponent 
        open = {groupWarningPopUp}
        title = {"Warning: No Grouped Customers Found"}
        contentText = {"Service group is empty; do you wish to remove group?"}
        onConfirm = {handleGroupDelete}
        onCancel = {() => {
          setGroupWarningPopUp(false);
          // setBankCustomers(bankedStr?.split(",").map((item: any) => item.trim()));
        }}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />
      {/* Unsaved confirmation */}
      <DialogComponent 
        open = {unsavedChanges}
        title = {"Unsaved Changes"}
        contentText = {"Do you wish to save the changes ?"}
        onConfirm = {handleYesSaveChanges}
        onCancel = {handleNoSaveChanges}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />
      {/* Group Unsaved confirmation */}
      <DialogComponent 
        open = {unsavedGroupChanges}
        title = {"Unsaved Changes"}
        contentText = {"Do you wish to save the changes ?"}
        onConfirm = {handleGroupYesSaveChanges}
        onCancel = {handleGroupNoSaveChanges}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />
      {/* Delete confirmation */}
      <DialogComponent 
        open = {confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title = {"Confirm Deletion"}
        contentText = {"Are you sure you wish to delete the Bank ?"}
        onConfirm = {() => {
          handleDelete();
          setConfirmDelete(false);
        }}
        onCancel = {() => {
          setConfirmDelete(false);
        }}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />

      <DialogComponent 
        open = {groupConfirmDelete}
        onClose={() => setGroupConfirmDelete(false)}
        title = {"Confirm Deletion"}
        contentText = {"Are you sure you wish to delete the Group ?"}
        onConfirm = {() => {
          handleGroupDelete();
          setGroupConfirmDelete(false);
        }}
        onCancel = {() => {
          setGroupConfirmDelete(false);
        }}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />
      {/* Confirm Dialog */}
      <DialogComponent 
        open = {Confirmpop}
        onClose={() => setConfirmpop(false)}
        title = {"Confirm Dialog"}
        contentText = {"Are you sure to want switch page ?"}
        onConfirm = {() => {
          handleSave();
          setConfirmpop(false);
        }}
        onCancel = {() => {
          setConfirmpop(false);
        }}
        confirmText = {"Yes"}
        cancelText = {"No"}
      />


      <Dialog open={ConfirmpopUpdate} onClose={() => setUpdateConfirmpop(false)}>
        <DialogTitle>Customer Updated Successfully</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setUpdateConfirmpop(false);
            }}
            color="primary"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      
      <Dialog open={ErrorPop} onClose={() => setErrorPop(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <DialogContentText>PLease try again</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setErrorPop(false);
            }}
            color="primary"
          >
            Okay
          </Button>

          {/* <Button onClick={()=>{setConfirmpop(false);}} color="primary" autoFocus>
              No
            </Button> */}
        </DialogActions>
      </Dialog>

      <Dialog open={ConfirmDuplicateCust} onClose={() => setUpdateConfirmDuplicateCust(false)}>
        <DialogTitle>
          One or more customers already belongs to another bank. Please remove them from the other bank first.
        </DialogTitle>
        <DialogContent>
          <ul>
            {DuplicateCust?.map((item: any) => (
              <li>{item}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUpdateConfirmDuplicateCust(false);
            }}
            color="primary"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ConfirmGroupDuplicateCust} onClose={() => setUpdateGroupConfirmDuplicateCust(false)}>
        <DialogTitle>
          One or more customers already belongs to another Group. Please remove them from the other Group first.
        </DialogTitle>
        <DialogContent>
          <ul>
            {groupDuplicateCust?.map((item: any) => (
              <li>{item}</li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUpdateGroupConfirmDuplicateCust(false);
            }}
            color="primary"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateGroupName} onClose={() => setDuplicateGroupName(false)}>
        <DialogTitle>
          Group name already exists for the sales org, Please try entering the different group name.
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              if(!editGroupLabel){
                 setOpenGroupNameDialog(true);
              }
              setDuplicateGroupName(false);
            }}
            color="primary"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateBankName} onClose={() => setDuplicateBankName(false)}>
        <DialogTitle>
          Bank name already exists for the sales org, Please try entering the different bank name.
        </DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              if(!editBankLabel){
                setOpenBankNameDialog(true);
              }
              setDuplicateBankName(false);
            }}
            color="primary"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );

  if (props.headerDisabled) return content;
  let title = "Customer";
  if (state) {
    title += state.deliveringPlant ? ` / ${state.deliveringPlant}` : ``;
    title += state.name1 || state.name2 || state.name3 ? ` /` : ``;
    title += state.name1 ? ` ${state.name1}` : ``;
    title += state.name2 ? ` ${state.name2}` : ``;
    title += state.name3 ? ` [${state.name3}]` : ``;
  }
  return <ResponsiveDrawer title={title}>{content}</ResponsiveDrawer>;
}
